import { WithId } from "mongodb";
import { DBPosition } from "../../../../api/types";
import { useMemo } from "react";

export type DataSetType = {
  x: number;
  y: number;
}[];

export type createGraphFromPositionsProps = {
  positions: WithId<DBPosition>[];
  minDistanceInMinutes: number;
  positionEntryCallback: (
    dataset: DataSetType,
    position: DBPosition,
    minTs: number,
  ) => { minTs: number };
  lastEntryCallback: (dataset: DataSetType, minTs: number) => void;
  initCallback?: (dataset: DataSetType) => void;
  // When set, the x-axis starts here
  rangeStartTs?: number;
  // Draw a flat line from rangeStartTs to the first point (only makes sense
  // when the account already existed before the range start)
  extendToRangeStart?: boolean;
};

export type createGraphFromPositionsResult = {
  dataset: DataSetType;
  minTs: number;
  maxTs: number;
  ticks: number[];
};

const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_TICKS = 10;

// One tick per local midnight, thinned to every n-th day for long ranges
export const getDayTicks = (minTs: number, maxTs: number): number[] => {
  const first = new Date(minTs);
  first.setHours(0, 0, 0, 0);
  if (first.getTime() < minTs) first.setDate(first.getDate() + 1);

  const days = Math.ceil((maxTs - first.getTime()) / DAY_MS);
  const step = Math.max(1, Math.ceil(days / MAX_TICKS));

  const ticks: number[] = [];
  for (const d = first; d.getTime() <= maxTs; d.setDate(d.getDate() + step)) {
    ticks.push(d.getTime());
  }
  return ticks;
};

export const useCreateGraphFromPositions = ({
  positions,
  minDistanceInMinutes,
  positionEntryCallback,
  lastEntryCallback,
  initCallback,
  rangeStartTs,
  extendToRangeStart,
}: // context,
createGraphFromPositionsProps): createGraphFromPositionsResult => {
  const graphData = useMemo(() => {
    const rawDataset: DataSetType = [];
    let minTs = Number.MAX_SAFE_INTEGER;

    initCallback?.(rawDataset);

    positions.forEach((position) => {
      let { minTs: newMinTs } = positionEntryCallback(
        rawDataset,
        position,
        minTs,
      );

      minTs = newMinTs;
    });

    const now = Date.now();
    lastEntryCallback(rawDataset, minTs);

    const MIN_DISTANCE_BETWEEN_POINTS = minDistanceInMinutes * 60 * 1000;

    let lastPointTs = 0;
    const dataset = rawDataset
      .sort((a, b) => a.x - b.x)
      .filter((item, index, self) => {
        if (index === 0 || index === self.length - 1) {
          lastPointTs = item.x;
          return true;
        }
        if (item.x - lastPointTs > MIN_DISTANCE_BETWEEN_POINTS) {
          lastPointTs = item.x;
          return true;
        }
        return false;
      });

    if (rangeStartTs !== undefined) {
      if (extendToRangeStart && dataset.length && dataset[0].x > rangeStartTs) {
        dataset.unshift({ x: rangeStartTs, y: dataset[0].y });
      }
      minTs = rangeStartTs;
    }

    return { dataset, minTs, maxTs: now, ticks: getDayTicks(minTs, now) };
  }, [positions, rangeStartTs, extendToRangeStart]);

  return graphData;
};
